/**
 * looseRelationshipEditorDialogStore — global open/close state for the
 * `LooseRelationshipEditorDialog` so any surface (grid header FK glyph,
 * MetaInfo manager row, settings list, …) can launch the same editor
 * without prop-drilling.
 *
 * Mirrors the JSON inspector store pattern (`jsonInspectorDialogStore`):
 * the dialog mounts once at the app level via
 * `GlobalLooseRelationshipEditorDialog` and opens on demand.
 *
 * Callers pass either `existingRelationshipId` (edit mode) or nothing
 * (add-another mode). The Global wrapper resolves the matching
 * `LooseRelationship` from MetaInfo to pre-fill the picker, so callers
 * don't need to pre-fetch the relationship themselves.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';

export type LooseRelationshipEditorContext = {
  connectionId: string;
  database: string;
  engine?: DatabaseEngineId;
  /** Source column being operated on — locked in the picker. */
  source: { schema: string; table: string; name: string; dataType: string };
  /**
   * When set, the dialog opens in edit mode for the matching relationship.
   * When undefined, opens in add-another mode (source stays locked, target empty).
   */
  existingRelationshipId?: string;
  /** Optional pre-fill for JSON-pathed columns in add mode. */
  initialSourcePath?: string;
  /** Analytics entry-point label ('header_glyph', 'metainfo_manager_details', …). */
  source_label?: string;
  metadataEditors?: TableExplorerMetadataEditorProviders;
};

type State = {
  isOpen: boolean;
  context: LooseRelationshipEditorContext | null;
};

type Actions = {
  openDialog: (ctx: LooseRelationshipEditorContext) => void;
  closeDialog: () => void;
};

export const useLooseRelationshipEditorDialogStore = create<State & Actions>(
  (set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Defer one frame so any in-flight overlay close (context menu, etc.)
      // finishes before the Dialog mounts — matches the JSON inspector
      // store's mitigation against the Radix overlay pointer-events race.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    closeDialog: () => set({ isOpen: false }),
  }),
);

export const openLooseRelationshipEditorDialog = (
  ctx: LooseRelationshipEditorContext,
) => useLooseRelationshipEditorDialogStore.getState().openDialog(ctx);
